import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { DEFAULT_WEIGHTS, describeWeights, explainScore, validateMatch } from '@crossmatch/shared';
import type { MatchResult } from '@crossmatch/shared';
import { matchApi } from '../api/match.js';
import type { MatchRequestPayload, MatchResponseDto } from '../api/types.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Collapsible } from '../components/Collapsible.js';
import { EmptyState } from '../components/EmptyState.js';
import { AiSuggestCard } from '../components/AiSuggestCard.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { ReasonList } from '../components/ReasonList.js';
import { AiSummaryBlock } from '../components/AiSummary.js';
import { ComponentRadarChart, ScoreBarChart } from '../components/ScoreChart.js';
import { ScoreBar } from '../components/ScoreBar.js';
import { SkillPicker } from '../components/SkillPicker.js';
import { Tag } from '../components/Tag.js';
import { formatPercentile, formatScore, schoolLabel, skillLabel } from '../lib/format.js';
import { useCandidatesQuery } from '../hooks/queries.js';
import { CandidatesPanel } from './Candidates.js';

interface ResultCardProps {
  result: MatchResult;
  rank: number;
  expanded: boolean;
  onToggle: (userId: string) => void;
  /** 本次匹配的需求上下文，原样传给 AI 解读接口 */
  requiredSkills: string[];
  weeklyHoursNeeded?: number;
}

function ResultCard({ result, rank, expanded, onToggle, requiredSkills, weeklyHoursNeeded }: ResultCardProps) {
  const coveredCount = result.covered.length;
  const missingCount = result.missing.length;
  return (
    <article className={rank === 1 ? 'card result-card result-card-top' : 'card result-card'}>
      {rank === 1 ? (
        <span className="result-ribbon" aria-hidden="true">
          推荐
        </span>
      ) : null}
      <div className="result-head">
        <span className="result-rank" aria-label={`第 ${rank} 名`}>
          {rank}
        </span>
        <div className="grow">
          <div className="result-name">{result.name}</div>
          <div className="row-tight">
            <Tag tone="school">{schoolLabel(result.school)}</Tag>
            <Tag tone={missingCount === 0 ? 'ok' : 'warn'}>
              命中需求 {coveredCount} 项{missingCount > 0 ? ` · 缺 ${missingCount} 项` : ''}
            </Tag>
          </div>
        </div>
        <div className="result-score">
          <div className="result-total">
            {formatScore(result.total)}
            <span className="result-total-unit"> 分</span>
          </div>
          <div className="result-percentile">池内百分位 {formatPercentile(result.percentile)}</div>
        </div>
      </div>

      <div className="grid-2">
        <ScoreBar label="需求覆盖" value={result.breakdown.coverage} weight={DEFAULT_WEIGHTS.coverage} />
        <ScoreBar label="团队互补" value={result.breakdown.complement} weight={DEFAULT_WEIGHTS.complement} />
        <ScoreBar label="时间协同" value={result.breakdown.time} weight={DEFAULT_WEIGHTS.time} />
        <ScoreBar label="跨校" value={result.breakdown.crossSchool} weight={DEFAULT_WEIGHTS.crossSchool} />
      </div>

      <AiSummaryBlock result={result} requiredSkills={requiredSkills} weeklyHoursNeeded={weeklyHoursNeeded} />

      <div className="row">
        <Button variant="secondary" size="sm" aria-expanded={expanded} onClick={() => onToggle(result.userId)}>
          {expanded ? '收起理由与分量' : '展开理由与分量'}
        </Button>
        {missingCount > 0 ? (
          <span className="field-hint">
            缺：
            {result.missing.map((skillId) => skillLabel(skillId)).join('、')}
          </span>
        ) : (
          <span className="field-hint">需求技能全部命中</span>
        )}
      </div>

      {expanded ? (
        <div className="result-panel">
          <ReasonList reasons={result.reasons} explain={explainScore(result)} title="后端给出的理由（原文照搬）" />
          <div className="grid-2">
            <div className="stack-sm">
              <span className="card-sub">缺失的需求技能（后端 missing）</span>
              <div className="row-tight">
                {result.missing.length === 0 ? (
                  <Tag tone="ok">无缺失</Tag>
                ) : (
                  result.missing.map((skillId) => (
                    <Tag key={skillId} tone="danger">
                      {skillLabel(skillId)}
                    </Tag>
                  ))
                )}
              </div>
              <span className="card-sub">命中的需求技能（后端 covered）</span>
              <div className="row-tight">
                {result.covered.length === 0 ? (
                  <Tag tone="warn">无命中</Tag>
                ) : (
                  result.covered.map((skillId) => (
                    <Tag key={skillId} tone="ok">
                      {skillLabel(skillId)}
                    </Tag>
                  ))
                )}
              </div>
            </div>
            <ComponentRadarChart result={result} />
          </div>
          <p className="chart-note">
            分量值由后端 MatchResult.breakdown 提供（0–1），此处仅按百分比画图，前端不重算任何分数。
          </p>
        </div>
      ) : null}
    </article>
  );
}

/** 匹配页：分数/分量/理由/缺失技能全部来自后端 MatchResult；页面显著位置声明「排序分，不是准确率」。 */
/** embedded：嵌入「找队友」聚合页时不渲染 page-head 与内部视图切换（Tab 由聚合页提供）。 */
export function MatchPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [teamSkills, setTeamSkills] = useState<string[]>([]);
  const [weeklyHoursText, setWeeklyHoursText] = useState('10');
  const [eventCode, setEventCode] = useState('');
  const [allowCrossSchool, setAllowCrossSchool] = useState(true);
  const [limitText, setLimitText] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [lastPayload, setLastPayload] = useState<MatchRequestPayload | null>(null);
  const [view, setView] = useState<'match' | 'candidates'>('match');

  const candidatesQuery = useCandidatesQuery();
  const poolSize = candidatesQuery.data?.candidates.length ?? null;

  const run = useMutation<MatchResponseDto, Error, MatchRequestPayload>({
    mutationFn: (payload) => matchApi.run(payload),
    onSuccess: () => {
      setFormError(null);
      // 默认全部收起：结果卡首屏只留姓名+分数+四条分量条，理由点开再看（演示时逐条展开更有节奏）
      setExpanded([]);
    },
    onError: (error) => {
      setFormError(`匹配失败：${toUserMessage(error)}`);
    },
  });

  const results = run.data?.results ?? [];
  const weightsText = run.data?.weights ?? describeWeights(DEFAULT_WEIGHTS);

  const toggleExpanded = (userId: string): void => {
    setExpanded((prev) => (prev.includes(userId) ? prev.filter((item) => item !== userId) : [...prev, userId]));
  };

  const buildPayload = (): MatchRequestPayload | { error: string } => {
    const limitTrimmed = limitText.trim();
    const limit = limitTrimmed.length > 0 ? Number(limitTrimmed) : undefined;
    if (limit !== undefined && (!Number.isInteger(limit) || limit <= 0 || limit > 100)) {
      return { error: '无法开始匹配：返回条数需为 1–100 的整数（留空表示不限制）' };
    }
    const payload: MatchRequestPayload = { requiredSkills, allowCrossSchool };
    if (eventCode.trim().length > 0) payload.eventCode = eventCode.trim().toUpperCase();
    if (teamSkills.length > 0) payload.teamSkills = teamSkills;
    const hoursText = weeklyHoursText.trim();
    if (hoursText.length > 0) {
      const hours = Number(hoursText);
      if (!Number.isFinite(hours) || hours < 0 || hours > 60) {
        return { error: '无法开始匹配：每周需求时间需在 0–60 小时之间（留空表示不按时间打分）' };
      }
      payload.weeklyHoursNeeded = hours;
    }
    if (limit !== undefined) payload.limit = limit;

    // 与后端同一套校验规则（shared/validateMatch），避免"前端放过去、后端才报错"
    const validated = validateMatch({
      requiredSkills: payload.requiredSkills,
      teamSkills: payload.teamSkills,
      weeklyHoursNeeded: payload.weeklyHoursNeeded,
      eventCode: payload.eventCode,
      allowCrossSchool: payload.allowCrossSchool,
      limit: payload.limit,
    });
    if (!validated.ok) return { error: `无法开始匹配：${validated.issues.map((issue) => issue.message).join('；')}` };
    return payload;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const payload = buildPayload();
    if ('error' in payload) {
      setFormError(payload.error);
      return;
    }
    setFormError(null);
    setLastPayload(payload);
    run.mutate(payload);
  };

  const topResults = useMemo(() => results.slice(0, 8), [results]);

  return (
    <div className="stack">
      {embedded ? null : (
        <div className="page-head">
          <div>
            <h1 className="page-title">开始匹配</h1>
            <p className="page-sub">填项目需要的技能与每周投入，匹配引擎排序，每条推荐都给出可解释理由。</p>
          </div>
          {poolSize !== null ? (
            <Tag tone={poolSize === 0 ? 'warn' : 'primary'}>候选池：{poolSize} 人</Tag>
          ) : null}
        </div>
      )}

      {embedded ? null : (
        <div className="tab-scroll" role="tablist" aria-label="匹配视图切换">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'match'}
            className={view === 'match' ? 'chip on' : 'chip'}
            onClick={() => setView('match')}
          >
            开始匹配
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'candidates'}
            className={view === 'candidates' ? 'chip on' : 'chip'}
            onClick={() => setView('candidates')}
          >
            候选人池
          </button>
          <span className="field-hint">
            想直接挑队伍？去<Link to="/hall"> 组队大厅 </Link>按互补度申请加入。
          </span>
        </div>
      )}

      {view === 'candidates' && !embedded ? (
        <CandidatesPanel />
      ) : (
        <>
      <Collapsible
        title="评分口径说明"
        hint="排序分，不是准确率 / 概率 / 成功率"
      >
        <p className="small">
          分数 0–100，只用于把候选人从高到低排队；「池内百分位」表示在<strong>本次候选池</strong>里不高于该分数的人占比，换一批候选池数字就会变。
        </p>
        <p className="small muted">计分公式（后端 v1.2）：{weightsText}。</p>
      </Collapsible>

      <Card title="项目需求" subtitle="最多选 30 个技能；留空就用所选赛事已配置的需求。">
        <form className="stack" onSubmit={handleSubmit} noValidate>
          {formError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{formError}</span>
            </div>
          ) : null}

          <SkillPicker
            selected={requiredSkills}
            onChange={setRequiredSkills}
            label="项目需要的技能"
            max={30}
          />

          <div className="grid-2">
            <div className="field">
              <label className="field-label" htmlFor="match-hours">
                每周需要投入（小时，0–60；留空 = 不按时间打分）
              </label>
              <input
                id="match-hours"
                className="input"
                type="number"
                min={0}
                max={60}
                step={1}
                value={weeklyHoursText}
                onChange={(event) => setWeeklyHoursText(event.target.value)}
              />
              <span className="field-hint">候选人每周可投入时间越接近该值，「时间协同」分量越高。</span>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="match-event-code">
                赛事码（选填，用于池子隔离）
              </label>
              <input
                id="match-event-code"
                className="input"
                value={eventCode}
                placeholder="如 EV2026"
                onChange={(event) => setEventCode(event.target.value.toUpperCase())}
              />
              <span className="field-hint">
                {poolSize !== null
                  ? `留空则匹配当前可见的全部候选人（授权后后端按赛事码过滤）。当前池：${poolSize} 人。`
                  : '留空则匹配当前可见的全部候选人。'}
              </span>
            </div>
          </div>

          <details className="card tight">
            <summary style={{ cursor: 'pointer' }}>高级选项：队伍已有技能 / 跨校开关 / 返回条数</summary>
            <div className="stack" style={{ marginTop: '12px' }}>
              <SkillPicker
                selected={teamSkills}
                onChange={setTeamSkills}
                label="队伍已有技能（选填，用于互补度）"
                hint="填了才会算「团队互补 = 1 − 与队伍技能结构的余弦相似度」；留空则取中性值 0.5。"
                max={30}
              />
              <div className="grid-2">
                <div className="field">
                  <label className="checkbox" htmlFor="match-cross-school">
                    <input
                      id="match-cross-school"
                      type="checkbox"
                      checked={allowCrossSchool}
                      onChange={(event) => setAllowCrossSchool(event.target.checked)}
                    />
                    允许跨校匹配（关闭后「跨校」分量计 0）
                  </label>
                  <span className="field-hint">跨校会带来多样性加分；如果比赛要求同校组队，请关闭。</span>
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="match-limit">
                    返回条数上限（选填，1–100）
                  </label>
                  <input
                    id="match-limit"
                    className="input"
                    type="number"
                    min={1}
                    max={100}
                    value={limitText}
                    placeholder="留空 = 不截断"
                    onChange={(event) => setLimitText(event.target.value)}
                  />
                </div>
              </div>
            </div>
          </details>

          <div className="row">
            <Button type="submit" variant="primary" loading={run.isPending}>
              {run.isPending ? '正在排序…' : '开始匹配'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setRequiredSkills([]);
                setTeamSkills([]);
                setWeeklyHoursText('10');
                setEventCode('');
                setAllowCrossSchool(true);
                setLimitText('');
                setFormError(null);
                run.reset();
              }}
              disabled={run.isPending}
            >
              重置
            </Button>
          </div>
        </form>
      </Card>

      {run.isPending ? <Loading label="正在让后端计算排序分与理由…" rows={4} /> : null}

      {run.isError ? (
        <ErrorState
          error={run.error}
          title="匹配失败"
          onRetry={() => {
            if (lastPayload) run.mutate(lastPayload);
          }}
        />
      ) : null}

      {!run.isPending && !run.isError && run.data === undefined ? (
        <EmptyState
          icon="⇅"
          title="还没有匹配结果"
          description={
            poolSize === 0
              ? '候选池现在是空的：先去「候选人池」录入至少 1 位候选人，再回来点「开始匹配」。'
              : '在上面的表单里选好项目需要的技能，点「开始匹配」，这里会按分数从高到低列出候选人。'
          }
          action={
            poolSize === 0 ? (
              <Link className="btn btn-primary" to="/candidates">
                去录入候选人
              </Link>
            ) : (
              <Button variant="secondary" onClick={() => setFormError('请先在上方选择「项目需要的技能」，再点「开始匹配」')}>
                我需要先填什么？
              </Button>
            )
          }
        />
      ) : null}

      {!run.isPending && run.data !== undefined ? (
        <>
          {run.data.aiSuggestions.length > 0 ? (
            <Card
              title="AI 补位建议"
              subtitle="候选池较小，以下是按你的需求生成的补位建议（非真实候选人，不参与打分）。"
            >
              <div className="result-list">
                {run.data.aiSuggestions.map((suggestion) => (
                  <AiSuggestCard key={suggestion.userId} suggestion={suggestion} />
                ))}
              </div>
            </Card>
          ) : null}

          {run.data.results.length === 0 ? (
            <EmptyState
              icon="∅"
              title="候选池里没有可匹配的人"
              description={
                poolSize === 0
                  ? '候选池是空的：先去「候选人池」录入候选人。'
                  : '当前筛选条件下没有候选人命中。可以检查赛事码是否写对，或清空赛事码后再试。'
              }
              action={
                <Link className="btn btn-primary" to="/candidates">
                  去候选人池
                </Link>
              }
            />
          ) : (
            <div className="stack">
              <Card
                title={`匹配结果（${run.data.results.length} 人，按分数降序）`}
                subtitle={`权重：${run.data.weights}。分数是排序分，不是准确率。`}
              >
              {topResults.length >= 2 ? (
                <>
                  <ScoreBarChart results={topResults} />
                  <p className="chart-note">
                    上图仅把后端返回的 total 画成柱状图，方便对比；不含任何前端计算。
                  </p>
                </>
              ) : null}
            </Card>

            <div className="result-list">
              {run.data.results.map((result, index) => (
                <ResultCard
                  key={result.userId}
                  result={result}
                  rank={index + 1}
                  expanded={expanded.includes(result.userId)}
                  onToggle={toggleExpanded}
                  requiredSkills={lastPayload?.requiredSkills ?? requiredSkills}
                  weeklyHoursNeeded={lastPayload?.weeklyHoursNeeded}
                />
              ))}
            </div>

            <p className="chart-note">
              分量条显示的是后端 MatchResult.breakdown（0–1 → 百分比）；权重标签为默认权重配置（
              {describeWeights(DEFAULT_WEIGHTS)}），若后端用自定义权重，请以后端返回的 weights 说明为准。
            </p>
          </div>
          )}
        </>
      ) : null}
        </>
      )}
    </div>
  );
}